import { css } from '@emotion/react';
import { TripType, CepData, BookingFlow } from '@flysas-tech/book-value-stream-models';
import { BREAKPOINTS, Button, Checkbox, Input, tokens } from '@flysas-tech/commercial-ui';
import { t } from 'i18n';
import { useState } from 'react';
import { PassengerCounts } from 'shared';

import { PassengerCountSelector } from './PassengerCountSelector';
import { FormErrors, getErrorMessage, validateCepData, validateLocation } from './utils';

const styles = {
  topContainer: css`
    width: 100%;
    display: flex;
    gap: ${tokens.dimension['space-2']};
    margin-bottom: ${tokens.dimension['space-4']};
    flex-direction: column;

    @media (min-width: ${BREAKPOINTS.layout.md}px) {
      flex-direction: row;
    }
  `,
  topSection: css`
    flex: 1;
    width: 100%;
    display: flex;
    gap: ${tokens.dimension['space-2']};
    flex-direction: column;

    > * {
      flex: 1;
    }

    @media (min-width: ${BREAKPOINTS.layout.sm}px) {
      flex-direction: row;
    }
  `,
  bottomContainer: css`
    display: flex;
    justify-content: space-between;
  `,
  bottomSection: css`
    display: flex;
    gap: ${tokens.dimension['space-2']};
  `,
  formError: css`
    color: ${tokens.color.red[50]};
  `,
  checkboxes: css`
    display: flex;
    align-items: center;
    gap: ${tokens.dimension['space-2']};
  `,
};

type Props = {
  data: Partial<CepData>;
  bookingFlow: BookingFlow;
  handleSearch: (data: CepData) => void;
};

export function Cep({ data, handleSearch, bookingFlow }: Props) {
  const [fields, setFields] = useState<Partial<CepData>>(data);
  const [formErrors, setFormErrors] = useState<FormErrors>({});

  const origin = fields.origin ?? '';
  const destination = fields.destination ?? '';
  const tripType = fields.tripType ?? TripType.ONE_WAY;
  const outboundDate = fields.outboundDate ?? '';
  const inboundDate = fields.inboundDate ?? '';
  const passengerCounts = fields.passengerCounts ?? { adults: 1, children: 0, infants: 0, youths: 0 };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const validation = validateCepData(fields, bookingFlow);
    if (validation.isValid) {
      handleSearch(validation.data);
    } else {
      setFormErrors(validation.errors);
    }
  };

  const isOneWay = tripType === TripType.ONE_WAY;
  const formErrorMessage = formErrors.formError;

  return (
    <form onSubmit={handleSubmit}>
      <div css={styles.topContainer}>
        <div css={styles.topSection}>
          <Input
            label={t('common:from', { defaultValue: 'From' })}
            value={origin}
            onChange={e => setFields(prev => ({ ...prev, origin: e.target.value }))}
            onBlur={() =>
              setFormErrors(prev => ({
                ...prev,
                origin: validateLocation(destination),
              }))
            }
            errorMessage={getErrorMessage(formErrors.destination)}
            autoComplete="on"
          />
          <Input
            label={t('common:to', { defaultValue: 'To' })}
            value={destination}
            onChange={e => setFields(prev => ({ ...prev, destination: e.target.value }))}
            onBlur={() =>
              setFormErrors(prev => ({
                ...prev,
                destination: validateLocation(destination),
              }))
            }
            errorMessage={getErrorMessage(formErrors.destination)}
            autoComplete="on"
          />
        </div>

        <div css={styles.topSection}>
          <Input
            label={t('common:outboundDate', { defaultValue: 'Outbound date' })}
            value={outboundDate}
            type="date"
            onChange={e => setFields(prev => ({ ...prev, outboundDate: e.target.value }))}
            onBlur={() =>
              setFormErrors(prev => ({
                ...prev,
                outboundDate: undefined,
                formError: undefined,
              }))
            }
            errorMessage={getErrorMessage(formErrors.outboundDate)}
            autoComplete="on"
          />

          {!isOneWay && (
            <Input
              label={t('common:returnDate', { defaultValue: 'Return Date' })}
              value={inboundDate}
              type="date"
              onChange={e => setFields(prev => ({ ...prev, inboundDate: e.target.value }))}
              onBlur={() =>
                setFormErrors(prev => ({
                  ...prev,
                  inboundDate: undefined,
                  formError: undefined,
                }))
              }
              errorMessage={getErrorMessage(formErrors.inboundDate)}
              autoComplete="on"
            />
          )}

          <PassengerCountSelector
            value={passengerCounts}
            onChange={(passengerCounts: PassengerCounts) => setFields(prev => ({ ...prev, passengerCounts }))}
            errorMessage={getErrorMessage(formErrors.passengerCounts)}
          />
        </div>
      </div>

      <div css={styles.bottomContainer}>
        <div css={styles.bottomSection}>
          <div css={styles.checkboxes}>
            <Checkbox
              label={t('common:roundTrip', { defaultValue: 'Round trip' })}
              checked={tripType === TripType.ROUND_TRIP}
              onChange={e =>
                setFields(prev => ({
                  ...prev,
                  tripType: e.target.checked ? TripType.ROUND_TRIP : TripType.ONE_WAY,
                  inboundDate: e.target.checked ? inboundDate : undefined,
                }))
              }
            />
          </div>
        </div>

        {!!formErrorMessage && <div css={styles.formError}>{formErrorMessage}</div>}

        <div>
          <Button type="submit" size="large" variant="primary">
            {t('common:search', { defaultValue: 'Search' })}
          </Button>
        </div>
      </div>
    </form>
  );
}
